import { attackClip, CREATURE_BY_ID, CREATURE_SCALE, CREATURE_STATES, creatureLook, creatureTone, creatureVariant, type CreatureState } from "@eldrun/content";
import type { CreatureAttackEvent } from "@eldrun/protocol";
import { Group, type Object3D } from "three";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import { InstanceBatcher } from "../render/instance-batcher.ts";
import { QUALITY } from "../render/quality.ts";
import { ANIMATED_SET, AnimatedSet, type AnimatedCandidate } from "./animated-set.ts";
import { applyCreaturePose, CreatureAnimator } from "./creature-animation.ts";
import { createCreatureModel, creatureScale, type CreatureRig } from "./creature-model.ts";
import { ANIMATED_LAYER, isAnimatedType, type CreatureSkins, type SkinnedCreature } from "./creature-skins.ts";
import { planStrike, strikeActiveMs, strikeImpactAt, strikeTime, type Strike } from "./creature-strike.ts";
import { rayCylinder, type CrosshairAim } from "./crosshair-aim.ts";
import { CLIENT_REACH, type Looker } from "./harvest-target.ts";

/** Corpos além disto (m) não são desenhados: a neblina já os esconde. */
export const CREATURE_DRAW_RADIUS = 140;
/** A troca entre o boneco procedural e o modelo animado (ms): o animado se dissolve por cima do outro. */
export const SWAP_MS = 200;
const MAX_AIM_ANGLE = (50 * Math.PI) / 180;

/** O que a camada lê de cada criatura: o schema `Creature` (ou qualquer objeto com os mesmos campos). */
export interface CreatureLike {
  type: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  hp: number;
  hpMax: number;
  /** Índice em `CREATURE_STATES`. */
  state: number;
  /** Assombração reforçada (maior e com contorno). */
  elite?: boolean;
  /** Cópia do Guardião Rosa (sem sombra). */
  decoy?: boolean;
}

/** Posição interpolada de uma criatura (a cena lê do `Predict`, 100 ms no passado). */
export interface CreaturePosition {
  x: number;
  y: number;
  z: number;
  yaw: number;
}

interface CreatureView {
  rig: CreatureRig;
  animator: CreatureAnimator;
  type: string;
  elite: boolean;
  /** Cópia do Guardião Rosa: sem sombra (é assim que se acha a verdadeira). */
  decoy: boolean;
  state: CreatureState;
  /** Instante (s) em que o estado mudou, para as poses com tempo (surgir, enrolar, tombar). */
  since: number;
  x: number;
  z: number;
  /** Velocidade horizontal estimada pela própria interpolação (o schema não manda velocidade). */
  speed: number;
  /** Altura dos pés e escala da pose no último quadro desenhado (sombra-blob). */
  y: number;
  scale: number;
  visible: boolean;
  /** Distância da câmera (m) no último quadro. */
  distance: number;
  /** Modelo animado (F50) e a troca em andamento: entrando (dissolve 1 → 0) ou saindo (0 → 1). */
  skinned: SkinnedCreature | undefined;
  swap: { mode: "entra" | "sai"; at: number } | undefined;
  /** Golpe anunciado pelo servidor, com o impacto do clipe no acerto (`creature-strike`). */
  strike: Strike | undefined;
  /** Ataques começados (cada preparo, ou golpe sem preparo antes) e o ataque do último golpe planejado. */
  cycle: number;
  strikeCycle: number;
}

/** Estados que cancelam o golpe: o cambalear, a morte e os sumiços. */
const CANCELS_STRIKE: ReadonlySet<CreatureState> = new Set(["cambaleando", "morta", "dissolvendo", "sumindo", "vulneravel"]);
/** Estados em que o golpe vale. */
const STRIKING: ReadonlySet<CreatureState> = new Set(["preparando", "golpeando", "recuperando"]);

/** Quem recebe as sombras-blob do quadro (`BlobShadows`). */
export interface BlobSink {
  add(id: string, x: number, y: number, z: number, radius: number, priority?: number): void;
}

/**
 * Sombra-blob de uma criatura (F51): nenhuma na cópia do Guardião Rosa nem no corpo caído; o raio
 * do corpo (a reforçada maior, como o boneco) vezes a escala da pose (some junto ao se desfazer). A
 * voadora no ar, a `h` m do chão (`ground`), projeta como se estivesse a h/4 (o shader apaga a
 * sombra com a distância entre o chão e os pés): fraca e um pouco maior lá no alto e mais forte a
 * cada metro que desce, até o golpe do mergulho. Os Guardiões entram primeiro, para a verdadeira
 * nunca ficar sem.
 */
export function creatureBlob(type: string, state: CreatureState, y: number, elite: boolean, decoy: boolean, scale: number, ground = y): { y: number; radius: number; priority: number } | undefined {
  if (decoy || state === "morta") return undefined;
  const def = CREATURE_BY_ID.get(type);
  // O raio da tabela já tem `CREATURE_SCALE`; a reforçada cresce por cima, como o boneco.
  const grow = creatureScale(type, elite) / CREATURE_SCALE;
  const radius = Math.max(0.3, (def?.radius ?? 0.4) * 1.15) * grow * scale;
  const priority = def?.archetype === "guardiao" ? 1 : 0;
  if (!flyingNow(type, state)) return { y, radius, priority };
  const h = Math.max(0, y - ground);
  return { y: ground + h * 0.25, radius: radius * (1 + 0.06 * Math.min(h, 8)), priority };
}

/** O que a mira e as barras de vida precisam de cada criatura visível. */
export interface CreatureSnapshot {
  id: string;
  type: string;
  x: number;
  y: number;
  z: number;
  hp: number;
  hpMax: number;
  state: CreatureState;
  elite: boolean;
}

export function creatureState(index: number): CreatureState {
  return CREATURE_STATES[index] ?? "vagando";
}

/** Pode apanhar? (o servidor ignora golpes em quem surge, cava, some em fumaça, se desfaz ou já morreu) */
export function targetable(state: CreatureState): boolean {
  return state !== "morta" && state !== "cavando" && state !== "surgindo" && state !== "dissolvendo" && state !== "sumindo";
}

/** Voadora no ar (bate as asas): fora do pouso vulnerável, da morte e da dissolução. */
export function flyingNow(type: string, state: CreatureState): boolean {
  return CREATURE_BY_ID.get(type)?.haunt?.fly !== undefined && state !== "vulneravel" && state !== "morta" && state !== "dissolvendo";
}

/** Distância do jogador à borda do corpo da criatura. */
export function creatureEdgeDistance(looker: Looker, c: CreatureSnapshot): number {
  return Math.hypot(c.x - looker.x, c.z - looker.z) - (CREATURE_BY_ID.get(c.type)?.radius ?? 0.4);
}

/**
 * Criatura e recurso na mira: vale o mais perto. Uma criatura atrás da árvore não impede a coleta;
 * uma à frente dela vira golpe. `resourceEdge` é a distância à borda do recurso (ou `undefined`).
 */
export function creatureFirst(looker: Looker, creature: CreatureSnapshot | undefined, resourceEdge: number | undefined): CreatureSnapshot | undefined {
  if (!creature) return undefined;
  return resourceEdge === undefined || creatureEdgeDistance(looker, creature) <= resourceEdge ? creature : undefined;
}

/**
 * Criatura sob a cruz (F55): a primeira que o raio da câmera pega, antes do que o mundo deixa ver, ao
 * alcance do corpo (com o raio dela), e antes de `before` (o recurso sob a cruz). Sem nenhuma,
 * `pickCreature` (o cone do corpo) fica de reserva.
 */
export function rayPickCreature(ray: CrosshairAim, looker: Looker, creatures: readonly CreatureSnapshot[], before = Infinity): CreatureSnapshot | undefined {
  let best: CreatureSnapshot | undefined;
  let bestT = Math.min(before, ray.worldT + 0.05);
  for (let i = 0; i < creatures.length; i++) {
    const c = creatures[i] as CreatureSnapshot;
    if (!targetable(c.state)) continue;
    const def = CREATURE_BY_ID.get(c.type);
    const radius = def?.radius ?? 0.4;
    if (Math.hypot(c.x - looker.x, c.z - looker.z) > CLIENT_REACH + radius || Math.abs(c.y - looker.y) > 3) continue;
    const t = rayCylinder(ray.ox, ray.oy, ray.oz, ray.dx, ray.dy, ray.dz, ray.t0, bestT, c.x, c.z, radius, c.y, c.y + (def?.height ?? 1.6));
    if (t < bestT) {
      bestT = t;
      best = c;
    }
  }
  return best;
}

/**
 * Criatura sob a mira: dentro do alcance (com o raio do corpo), à frente (até 50° do olhar),
 * preferindo a mais alinhada e mais próxima. Diferente da coleta, não aceita quem está ao lado ou
 * atrás mesmo colado: o golpe do servidor só pega à frente. O servidor resolve o golpe de verdade;
 * isto só orienta o rótulo e a escolha entre golpear e coletar.
 */
export function pickCreature(looker: Looker, creatures: Iterable<CreatureSnapshot>): CreatureSnapshot | undefined {
  const fx = -Math.sin(looker.yaw);
  const fz = -Math.cos(looker.yaw);
  let best: CreatureSnapshot | undefined;
  let bestScore = Infinity;
  for (const c of creatures) {
    if (!targetable(c.state)) continue;
    const radius = CREATURE_BY_ID.get(c.type)?.radius ?? 0.4;
    const dx = c.x - looker.x;
    const dz = c.z - looker.z;
    const d = Math.hypot(dx, dz);
    if (d > CLIENT_REACH + radius || Math.abs(c.y - looker.y) > 3) continue;
    const dot = d > 1e-6 ? (dx * fx + dz * fz) / d : 1;
    const angle = Math.acos(Math.max(-1, Math.min(1, dot)));
    // Só dentro do próprio corpo dela o ângulo deixa de valer.
    if (angle > MAX_AIM_ANGLE && d > radius * 0.5) continue;
    const score = angle + d * 0.08;
    if (score < bestScore) {
      bestScore = score;
      best = c;
    }
  }
  return best;
}

/**
 * Criaturas na cena (F13): um boneco procedural por criatura do schema, posicionado pela
 * interpolação e animado pela pose do estado. Nada é simulado aqui; tudo vem do servidor.
 *
 * Desenho em lotes (F36): o boneco continua calculando as poses (grupos e articulações), mas fica
 * fora da cena; cada peça visível entra como instância no lote da sua geometria e material. O custo
 * em draw calls passa a ser o número de peças diferentes, não criaturas × peças (40 assombrações
 * custavam ~230 draw calls).
 *
 * Criaturas animadas (F50 e "mais criaturas animadas"): as mais perto dos tipos com modelo
 * (`CREATURE_LOOKS`), até `QUALITY.animated` (`AnimatedSet`), trocam o boneco por um corpo com skin e
 * clipes (`CreatureSkins`), uma draw call cada (mais uma com a peça acesa). As cópias do Guardião Rosa
 * entram como a verdadeira. A troca dissolve o animado por cima do boneco em `SWAP_MS` e copia a fase da
 * passada; a sombra-blob e as barras seguem a criatura, não o corpo. O golpe anunciado
 * (`strike`) põe o quadro do impacto do clipe no acerto do servidor. Sem o modelo (arquivo ausente,
 * erro, `skins` desligado), fica o boneco.
 */
export class CreatureLayer {
  private readonly batcher: InstanceBatcher;
  private readonly atmosphere: AtmosphereUniforms;
  private readonly skins: CreatureSkins | undefined;
  private readonly views = new Map<string, CreatureView>();
  private readonly present = new Set<string>();
  private readonly snapshots: CreatureSnapshot[] = [];
  private readonly animatedRoot = new Group();
  private readonly set = new AnimatedSet();
  private readonly candidates: AnimatedCandidate[] = [];
  private lastTick = -Infinity;
  private frame = 0;
  private drawn = 0;
  /** Avisado quando uma criatura muda de estado (efeitos: fumaça ao se desfazer). */
  onStateChange: ((snapshot: CreatureSnapshot) => void) | undefined;

  constructor(root: Object3D, atmosphere: AtmosphereUniforms, skins?: CreatureSkins) {
    this.batcher = new InstanceBatcher(root, "criaturas");
    this.atmosphere = atmosphere;
    this.skins = skins;
    this.animatedRoot.name = ANIMATED_LAYER;
    root.add(this.animatedRoot);
  }

  /**
   * Um quadro: cria e remove corpos conforme o schema, aplica posição interpolada e pose.
   * `now` em segundos. Devolve as criaturas do quadro (para mira e barras).
   */
  update<T extends CreatureLike>(
    creatures: { forEach(cb: (c: T, id: string) => void): void },
    position: (c: T) => CreaturePosition,
    dt: number,
    now: number,
    cameraX: number,
    cameraZ: number,
  ): readonly CreatureSnapshot[] {
    this.present.clear();
    this.snapshots.length = 0;
    this.candidates.length = 0;
    this.drawn = 0;
    this.frame++;
    const ms = now * 1000;
    // As candidatas só são montadas nos quadros da rodada (a cada 250 ms), não a cada quadro.
    const tick = this.skins !== undefined && ms - this.lastTick >= ANIMATED_SET.tickMs;
    creatures.forEach((c, id) => {
      this.present.add(id);
      const p = position(c);
      const state = creatureState(c.state);
      let view = this.views.get(id);
      if (!view) {
        const elite = c.elite === true;
        const decoy = c.decoy === true;
        view = { rig: createCreatureModel(this.atmosphere, c.type, elite), animator: new CreatureAnimator(), type: c.type, elite, decoy, state, since: now, x: p.x, z: p.z, speed: 0, y: p.y, scale: 1, visible: false, distance: Infinity, skinned: undefined, swap: undefined, strike: undefined, cycle: 0, strikeCycle: -1 };
        this.views.set(id, view);
      }
      const changed = state !== view.state;
      if (changed) {
        // Um ataque novo começa no preparo (ou num golpe sem preparo antes); o anúncio que chegou antes
        // do patch, ainda tocando, é o dele.
        if (state === "preparando" || (state === "golpeando" && view.state !== "preparando")) {
          view.cycle++;
          if (view.strike) view.strikeCycle = view.cycle;
        }
        view.state = state;
        view.since = now;
        if (CANCELS_STRIKE.has(state)) view.strike = undefined;
      }
      const instant = dt > 0 ? Math.hypot(p.x - view.x, p.z - view.z) / dt : 0;
      view.speed += (Math.min(instant, 15) - view.speed) * (1 - Math.exp(-dt * 10));
      view.x = p.x;
      view.z = p.z;
      view.distance = Math.hypot(p.x - cameraX, p.z - cameraZ);
      const visible = view.distance <= CREATURE_DRAW_RADIUS * QUALITY.distance;
      view.visible = visible;
      // O boneco procedural some de vez só com o animado inteiro na frente dele.
      const covered = view.skinned !== undefined && view.swap === undefined;
      view.rig.root.visible = visible && !covered;
      if (visible) {
        this.drawn++;
        view.rig.root.position.set(p.x, p.y, p.z);
        view.rig.root.rotation.y = p.yaw;
        const pose = view.animator.pose({ state, speed: view.speed, stateTime: now - view.since, flying: flyingNow(c.type, state) }, dt, view.rig.legLength);
        // Também escondido: na saída do animado, o boneco volta já na pose do quadro.
        applyCreaturePose(view.rig, pose);
        view.y = p.y;
        view.scale = pose.scale;
      }
      // A cópia do Guardião Rosa entra como a verdadeira (o mesmo tipo, a mesma prioridade e o mesmo
      // modelo): o corpo animado não a denuncia; só a falta de sombra.
      if (tick && this.skins && isAnimatedType(c.type)) {
        const def = CREATURE_BY_ID.get(c.type);
        // O Dragão anima até onde é desenhado: no Mergulho (45–70 m da Jangada) ele cruzaria os 45/52 m a
        // toda hora, e o boneco dele (a serpente de fogo) não se parece com o modelo.
        const far = def?.archetype === "final";
        if (view.distance <= (far ? ANIMATED_SET.far : ANIMATED_SET.promote) * QUALITY.distance) this.skins.request(c.type);
        this.candidates.push({ id, distance: view.distance, priority: def?.archetype === "chefe" || def?.archetype === "guardiao" || far ? 1 : 0, state, ready: this.skins.ready(c.type), ...(far && { far }) });
      }
      const snapshot = { id, type: c.type, x: p.x, y: p.y, z: p.z, hp: c.hp, hpMax: c.hpMax, state, elite: c.elite === true };
      this.snapshots.push(snapshot);
      if (changed) this.onStateChange?.(snapshot);
      if (view.skinned) this.placeSkinned(view, p, ms, dt);
    });
    for (const [id, view] of this.views) {
      if (this.present.has(id)) continue;
      this.dropSkinned(view);
      this.set.forget(id);
      this.views.delete(id);
    }
    if (tick) {
      this.lastTick = ms;
      this.retarget(ms, creatures, position);
    }
    this.draw();
    return this.snapshots;
  }

  /** Uma rodada do conjunto animado: troca quem entrou e quem saiu. */
  private retarget<T extends CreatureLike>(ms: number, creatures: { forEach(cb: (c: T, id: string) => void): void }, position: (c: T) => CreaturePosition): void {
    const changes = this.set.tick(ms, this.candidates, QUALITY.animated, QUALITY.distance);
    for (const id of changes.demote) {
      const view = this.views.get(id);
      if (!view?.skinned) continue;
      // O boneco volta na hora, na fase da passada do animado; o animado se dissolve por cima.
      view.animator.phase = view.skinned.machine.phase * Math.PI * 2;
      view.swap = { mode: "sai", at: ms };
      view.rig.root.visible = view.visible;
    }
    if (changes.promote.length === 0) return;
    // Nunca mais corpos que o teto, contando os que ainda saem dissolvendo: sem vaga agora, a entrada
    // espera a próxima rodada (os que saem somem em `SWAP_MS`, menos que uma rodada).
    let live = 0;
    for (const view of this.views.values()) if (view.skinned) live++;
    const promoted = new Set(changes.promote);
    creatures.forEach((c, id) => {
      if (!promoted.has(id)) return;
      const view = this.views.get(id);
      const look = creatureLook(c.type);
      if (!view || !look || !this.skins) return;
      if (view.skinned) {
        // Voltou antes de acabar de sair: só inverte a troca.
        view.swap = { mode: "entra", at: ms };
        return;
      }
      if (live >= QUALITY.animated) {
        this.set.refuse(id);
        return;
      }
      const def = CREATURE_BY_ID.get(c.type);
      const variant = creatureVariant(look, id)?.id ?? look.variants[0]!.id;
      const body = this.skins.acquire(c.type, variant, { elite: view.elite, night: def?.archetype === "assombracao", tone: creatureTone(def?.archetype ?? "neutra") }, def?.haunt?.fly !== undefined);
      if (!body) {
        this.set.refuse(id);
        return;
      }
      live++;
      view.skinned = body;
      view.swap = { mode: "entra", at: ms };
      // A passada continua de onde o boneco estava.
      body.machine.phase = (((view.animator.phase / (Math.PI * 2)) % 1) + 1) % 1;
      body.root.scale.setScalar(creatureScale(c.type, view.elite));
      this.animatedRoot.add(body.root);
      this.placeSkinned(view, position(c), ms, 0);
    });
  }

  /** O corpo animado no lugar, na pose do quadro e no dissolve (o da troca e o do estado, o maior). */
  private placeSkinned(view: CreatureView, p: CreaturePosition, ms: number, dt: number): void {
    const body = view.skinned!;
    let swap = 0;
    if (view.swap) {
      const k = Math.min(1, (ms - view.swap.at) / SWAP_MS);
      swap = view.swap.mode === "entra" ? 1 - k : k;
      if (k >= 1) {
        if (view.swap.mode === "sai") {
          this.dropSkinned(view);
          return;
        }
        view.swap = undefined;
        view.rig.root.visible = false;
      }
    }
    body.root.visible = view.visible;
    // Malha neon (F60): o tom (a fauna acende em laranja quando ataca) e a ameaça na preparação do golpe.
    const def = CREATURE_BY_ID.get(view.type);
    const striking = view.state === "preparando" || view.state === "golpeando";
    body.setLook({ elite: view.elite, night: def?.archetype === "assombracao", tone: creatureTone(def?.archetype ?? "neutra", striking) });
    body.setThreat(view.state === "preparando" ? 1 : view.state === "golpeando" ? 0.5 : 0);
    body.root.position.set(p.x, p.y, p.z);
    body.root.rotation.y = p.yaw;
    if (!view.strike && (view.state === "preparando" || view.state === "golpeando")) this.fallbackStrike(view, ms);
    // O golpe solto (uma marca anunciada com a criatura andando, sem preparo) toca por cima da locomoção.
    const strikeAt = view.strike && (STRIKING.has(view.state) || (view.strike.free === true && !CANCELS_STRIKE.has(view.state))) ? strikeTime(view.strike, ms) : undefined;
    if (view.strike && strikeAt === undefined && ms >= view.strike.endAt) view.strike = undefined;
    const mix = body.update(
      {
        signals: { state: view.state, stateTime: ms / 1000 - view.since, speed: view.speed, flying: flyingNow(view.type, view.state), ...(strikeAt !== undefined && { strike: { clip: view.strike!.clip, time: strikeAt, free: view.strike!.free === true } }) },
        dt,
        frame: this.frame,
        distance: view.distance,
        visible: view.visible,
        range: QUALITY.distance,
      },
      // Golpe, morte e dissoluções todo quadro (o dissolve não anda aos saltos do LOD).
      strikeAt !== undefined || view.state === "morta" || view.state === "dissolvendo" || view.state === "sumindo" || body.dissolve > 0,
    );
    body.setDissolve(Math.max(swap, mix.dissolve));
  }

  private dropSkinned(view: CreatureView): void {
    if (!view.skinned) return;
    this.skins?.release(view.skinned);
    view.skinned = undefined;
    view.swap = undefined;
    view.rig.root.visible = view.visible;
  }

  /**
   * Ataque anunciado (`CreatureAttackEvent`): o golpe da criatura com o quadro do impacto no acerto
   * (`agora + inMs − rtt/2`). `now` em ms (o relógio da cena), `rtt` em ms. Ataque sem clipe no modelo
   * (o tipo não é animado) é ignorado; o cambalear e a morte cancelam.
   */
  strike(e: Pick<CreatureAttackEvent, "id" | "attack" | "inMs" | "mark" | "activeMs">, now: number, rtt: number): void {
    const view = this.views.get(e.id);
    if (!view || !isAnimatedType(view.type) || !this.skins) return;
    const def = CREATURE_BY_ID.get(view.type)?.attacks.find((a) => a.id === e.attack);
    const clip = `golpe:${def ? attackClip(def) : e.attack}`;
    const info = (this.skins.table(view.type) ?? {})[clip];
    if (!def || !info) return;
    const impactAt = strikeImpactAt(def, e, now, rtt);
    if (impactAt === undefined) return;
    // Uma marca anunciada com a criatura fora do ataque (o cuspe do Dragão no Mergulho: o servidor só põe
    // a marca, sem preparo) toca solta, por cima da locomoção; as outras esperam o preparo, como na F50.
    const free = def.kind === "marcas" && !STRIKING.has(view.state);
    view.strike = { ...planStrike(clip, info, now, impactAt, def.recovery * 1000, strikeActiveMs(def, e)), ...(free && { free }) };
    view.strikeCycle = view.cycle;
  }

  /**
   * Preparo ou golpe sem anúncio (a criatura roendo uma construção, ou o ataque anunciado antes de ela
   * entrar no schema): o primeiro golpe dela, com o impacto no fim do preparo ou, já golpeando, agora.
   * Um anúncio que chegue depois o troca.
   */
  private fallbackStrike(view: CreatureView, ms: number): void {
    // Este ataque já teve o golpe (anunciado ou não), que acabou: não repete.
    if (view.strikeCycle === view.cycle) return;
    const def = CREATURE_BY_ID.get(view.type)?.attacks[0];
    const info = def && (this.skins?.table(view.type) ?? {})[`golpe:${attackClip(def)}`];
    if (!def || !info) return;
    const since = view.since * 1000;
    view.strike = planStrike(`golpe:${attackClip(def)}`, info, ms, view.state === "preparando" ? since + def.windup * 1000 : since, def.recovery * 1000);
    view.strikeCycle = view.cycle;
  }

  /** Junta as peças dos corpos visíveis nos lotes (um draw call por peça diferente). */
  private draw(): void {
    this.batcher.begin();
    for (const view of this.views.values()) this.batcher.addTree(view.rig.root);
    this.batcher.end();
  }

  /**
   * Sombras-blob das criaturas desenhadas no último quadro (sem a cópia do Guardião nem os corpos
   * caídos); `ground` dá a altura do chão (para as voadoras).
   */
  castBlobs(sink: BlobSink, ground: (x: number, z: number) => number): void {
    this.views.forEach((view, id) => {
      if (!view.visible) return;
      const blob = creatureBlob(view.type, view.state, view.y, view.elite, view.decoy, view.scale, flyingNow(view.type, view.state) ? ground(view.x, view.z) : view.y);
      if (blob) sink.add(id, view.x, blob.y, view.z, blob.radius, blob.priority);
    });
  }

  /** É a cópia do Guardião Rosa? (o e2e confere que nenhuma projeta sombra) */
  decoy(id: string): boolean {
    return this.views.get(id)?.decoy === true;
  }

  /** O boneco de uma criatura (poses; testes e depuração). */
  rig(id: string): CreatureRig | undefined {
    return this.views.get(id)?.rig;
  }

  /** Fase da passada do boneco procedural (rad; testes da troca). */
  walkPhase(id: string): number | undefined {
    return this.views.get(id)?.animator.phase;
  }

  /** O corpo animado de uma criatura, se houver (testes e vitrines). */
  animated(id: string): { body: SkinnedCreature; swapping: boolean; strike: Strike | undefined } | undefined {
    const view = this.views.get(id);
    return view?.skinned && { body: view.skinned, swapping: view.swap !== undefined, strike: view.strike };
  }

  /** Lotes com alguma instância no último quadro (= draw calls das criaturas). */
  get batchCount(): number {
    return this.batcher.count;
  }

  /** Criaturas no schema, desenhadas no último quadro, animadas e as trocas de corpo até aqui. */
  get stats(): { total: number; drawn: number; animated: number; swaps: number } {
    let animated = 0;
    for (const view of this.views.values()) if (view.skinned) animated++;
    return { total: this.views.size, drawn: this.drawn, animated, swaps: this.set.swaps };
  }

  dispose(): void {
    for (const view of this.views.values()) this.dropSkinned(view);
    this.views.clear();
    this.batcher.dispose();
    this.animatedRoot.removeFromParent();
  }
}
